import React, { useState } from 'react';
import { useEntitlements } from '../../contexts/EntitlementContext';
import { subscriptionService } from '../../services/subscriptionService';
import UpgradeModal from '../UpgradeModal';

export const LITE_UPGRADE_CTA_TEXT = 'Need more automation, lead generation, or sales tools? Upgrade LaunchPoint.';
const DISMISS_KEY = 'lp_lite_upgrade_cta_dismissed';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Low-key upgrade prompt for LaunchPoint Lite (included with Mamba websites). Lite must stay
 * fully useful on its own, so this is one quiet line — never a modal, gate, or interruption.
 */
const LiteUpgradeCta: React.FC<{ dismissible?: boolean }> = ({ dismissible = true }) => {
  const { getTier, getPlanCode } = useEntitlements();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [showModal, setShowModal] = useState(false);

  if (getTier() !== 'LITE' || (dismissible && dismissed)) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable: the prompt simply returns on next visit.
    }
  };

  const handleUpgrade = async (planCode: string) => {
    try {
      const response = await subscriptionService.upgradeSubscription(planCode);
      if ('checkout_url' in response && response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      console.error('Upgrade failed:', error);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface-muted px-4 py-3 text-sm">
        <p className="text-fg-secondary">{LITE_UPGRADE_CTA_TEXT}</p>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => setShowModal(true)} className="font-medium text-primary hover:underline">
            View upgrade options
          </button>
          {dismissible ? (
            <button type="button" onClick={dismiss} className="text-fg-muted hover:text-fg">
              Dismiss
            </button>
          ) : null}
        </div>
      </div>
      <UpgradeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onUpgrade={handleUpgrade}
        currentPlanCode={getPlanCode()}
      />
    </>
  );
};

export default LiteUpgradeCta;
